/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseEETicket.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Button, DialogActions, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import { componentStyle } from "../../../config/appTheme";
import RaiseTicketTemplate from "./RaiseTicketTemplate";
import SelectTemplate from "./SelectDialog/SelectTemplate";
import { useDispatch, useSelector } from "react-redux";
import DialogIndex from "./SelectDialog/DialogIndex";
import KeyValueDisplayCard from "./SelectDialog/KeyValueDisplayCard";
import CustomCheckBoxWithoutFormControl from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import RaiseTicketRightTemplate from "./RaiseTicketRightTemplate";
import { CAPABILITY_CODE } from "../../../config/appConstants";
import EESWMSavedLinkageCard from "./EESWMSavedLinkageCard";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import { HandleApiActions } from "../../../redux/actions/actionHandler";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";
import { ENTITY } from "../../../components/customComponentsV2/SmartMatchingDialogContainer/constants/entityTypes";
import AISuggestionDialogContainer from "../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import { labCar, legalRequirements, userManagementApis } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import TestCaseCampaignLinkDialog from "./TestCaseCampaignLinkDialog";
import TestCaseComponentLinkDialog from "./TestCaseComponentLinkDialog";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import SelectDialogActionButtons from "./SelectDialog/SelectDialogActionButtons";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";

const removeDuplicates = (arr = []) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Map();
  for (const user of arr) {
    if (!user?.user_id) continue;
    const key = String(user.user_id);
    if (seen.has(key)) {
      const existing = seen.get(key);
      if (
        user.component_description &&
        existing.component_description &&
        !existing.component_description.includes(user.component_description)
      ) {
        existing.component_description += ", " + user.component_description;
      }
    } else {
      seen.set(key, { ...user });
    }
  }
  return Array.from(seen.values());
};

const insertCapabilityCodeToUserList = (userDetails = [], allUsersList = []) => {
  if (!Array.isArray(userDetails)) return [];
  const userMap = new Map();
  for (const au of allUsersList || []) {
    if (au?.user_id) {
      userMap.set(String(au.user_id), au);
    }
  }
  return userDetails.map((u) => {
    const matchedUser = userMap.get(String(u?.user_id));
    const capCode =
      matchedUser?.role_details?.capability_code ||
      matchedUser?.capability_code ||
      u?.role_details?.capability_code ||
      u?.capability_code;
    const displayName =
      u?.name ||
      u?.fullname ||
      matchedUser?.fullname ||
      matchedUser?.name ||
      u?.email ||
      matchedUser?.email ||
      "";
    return {
      ...matchedUser,
      ...u,
      name: displayName,
      fullname: displayName,
      capability_code: capCode,
    };
  });
};

const gatherFromAll = (data = [], field = "", allUsersList = []) => {
  return (data || []).flatMap((ee) => {
    const users = ee?.[field];
    if (!users) return [];
    const userArray = Array.isArray(users) ? users : [users];
    return insertCapabilityCodeToUserList(userArray, allUsersList).map((u) => ({
      ...u,
      component_description: ee.description || ee.assembly_number || ee.name || "",
    }));
  });
};

const extractUsersFromEEData = (data = [], usersList = []) => {
  const eeList = Array.isArray(data) ? data : [data].filter(Boolean);
  const ownerDetails = [
    ...gatherFromAll(eeList, "owner_details", usersList),
    ...gatherFromAll(eeList, "owners", usersList),
  ];
  const coOwnerDetails = [
    ...gatherFromAll(eeList, "co_owner_details", usersList),
    ...gatherFromAll(eeList, "co_owners", usersList),
  ];
  const editorDetails = [
    ...gatherFromAll(eeList, "edit_user_details", usersList),
    ...gatherFromAll(eeList, "edit_users", usersList),
  ];
  const viewersDetails = [
    ...gatherFromAll(eeList, "view_user_details", usersList),
    ...gatherFromAll(eeList, "view_users", usersList),
  ];

  const duplicateRemovedComponentRequestor = removeDuplicates([
    ...ownerDetails,
    ...coOwnerDetails,
  ]);

  const requestors = duplicateRemovedComponentRequestor.filter((ele) => {
    return ele?.capability_code !== CAPABILITY_CODE.supplier;
  });

  const duplicateRemovedComponentSupplierDetails = removeDuplicates([
    ...coOwnerDetails,
    ...editorDetails,
    ...viewersDetails,
  ]);

  const suppliers = duplicateRemovedComponentSupplierDetails.filter((ele) => {
    return ele?.capability_code === CAPABILITY_CODE.supplier;
  });

  return { requestors, suppliers };
};

const hasSameEEs = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
  const set1 = new Set(arr1.map(Number));
  return arr2.every((id) => set1.has(Number(id)));
};

const replaceTestCaseIds = (testCases, idReplacementMap) => {
  if (!Array.isArray(testCases)) return testCases;
  return testCases.map((tc) => {
    const newId = idReplacementMap.get(String(tc?.test_case_id));
    return newId ? { ...tc, test_case_id: newId } : tc;
  });
};

const replaceIdInArray = (ids, idReplacementMap) => {
  if (!Array.isArray(ids)) return ids;
  return ids.map((id) => idReplacementMap.get(String(id)) ?? id);
};

const updateLegalItemWithNewIds = (legalItem, idReplacementMap) => {
  const updatedLegal = { ...legalItem };
  if (Array.isArray(updatedLegal.test_case_ids)) {
    updatedLegal.test_case_ids = replaceIdInArray(
      updatedLegal.test_case_ids,
      idReplacementMap,
    );
  }
  if (Array.isArray(updatedLegal.test_cases)) {
    updatedLegal.test_cases = replaceTestCaseIds(
      updatedLegal.test_cases,
      idReplacementMap,
    );
  }
  return updatedLegal;
};

const updateLinkageWithNewIds = (linkage, idReplacementMap) => {
  const updatedLinkage = { ...linkage };

  if (Array.isArray(updatedLinkage.test_case_ids)) {
    updatedLinkage.test_case_ids = replaceIdInArray(
      updatedLinkage.test_case_ids,
      idReplacementMap,
    );
  }

  if (Array.isArray(updatedLinkage.test_cases)) {
    updatedLinkage.test_cases = replaceTestCaseIds(
      updatedLinkage.test_cases,
      idReplacementMap,
    );
  }

  if (Array.isArray(updatedLinkage.legal)) {
    updatedLinkage.legal = updatedLinkage.legal.map((legalItem) =>
      updateLegalItemWithNewIds(legalItem, idReplacementMap),
    );
  }

  return updatedLinkage;
};

function RaiseEETicket({
  savedLinkages,
  setSavedLinkages,
  setDisableRaiseTicketButton,
  entityType,
}) {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [zoneList, setZoneList] = useState([]);
  const [isZonalProject, setIsZonalProject] = useState(false);
  const [openDuplicateAlert, setOpenDuplicateAlert] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState("");

  const [selectedEEMain, setSelectedEEMain] = useState([]);
  const [openEESelect, setOpenEESelect] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);

  const [selectedLegalMain, setSelectedLegalMain] = useState([]);
  const [selectedLegalForDisplay, setSelectedLegalForDisplay] = useState([]);
  const [disabledLegalRequirement, setDisabledLegalRequirement] = useState([]);
  const [openLegalSelect, setOpenLegalSelect] = useState(false);
  const [disabledEEComponents, setDisabledEEComponents] = useState([]);

  const [openTestCaseSelect, setOpenTestCaseSelect] = useState(false);
  const [selectedTestCaseForDisplay, setSelectedTestCaseForDisplay] =
    useState(null);
  const [selectedTestCaseMain, setSelectedTestCaseMain] = useState(null);
  const [selectedComponentRequestor, setSelectedComponentRequestor] = useState(
    [],
  );
  const [selectedLegalTechUserDetails, setSelectedLegalTechUserDetails] =
    useState([]);

  const [selectedComplianceRequestors, setSelectedComplianceRequestors] =
    useState([]);
  const [selectedComplianceReviewers, setSelectedComplianceReviewers] =
    useState([]);
  const [selectedComplianceSupplier, setSelectedComplianceSupplier] = useState(
    [],
  );
  const [description, setDescription] = useState("");
  const [
    selectedComponentSupplierDetails,
    setSelectedComponentSupplierDetails,
  ] = useState([]);
  const [selectedDynamicForm, setSelectedDynamicForm] = useState(null);

  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [
    selectedSmartMatchTestCaseSuggestions,
    setSelectedSmartMatchTestCaseSuggestions,
  ] = useState([]);
  const prefillContext = null;

  const [allUsersList, setAllUsersList] = useState([]);

  const [disableSmartMatch, setDisableSmartMatch] = useState(false);
  const [closeAiDialogOnAdd, setCloseAiDialogOnAdd] = useState(false);
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);

  const [openComponentLinkDialog, setOpenComponentLinkDialog] = useState(false);
  const [testCasesToComponentLink, setTestCasesToComponentLink] = useState([]);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [pendingEEComponent, setPendingEEComponent] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");

  const savedLinkagesRef = React.useRef(savedLinkages);
  useEffect(() => {
    savedLinkagesRef.current = savedLinkages;
  }, [savedLinkages]);

  const addFullyLinkedTestCases = (testCases = []) => {
    console.log("--addFullyLinkedTestCases---", testCases)
    if (!Array.isArray(testCases) || testCases.length === 0) return;

    const idReplacementMap = new Map();
    for (const testCase of testCases) {
      const oldId = testCase?.original_test_case_id || testCase?.test_case_id;
      const newId = testCase?.new_test_case_id || testCase?.test_case_id;
      if (oldId && newId && String(oldId) !== String(newId)) {
        idReplacementMap.set(String(oldId), String(newId));
      }
    }

    if (idReplacementMap.size > 0) {
      const updatedLinkages = (savedLinkagesRef.current || []).map((linkage) =>
        updateLinkageWithNewIds(linkage, idReplacementMap),
      );

      setSavedLinkages(updatedLinkages);
    }

    setSelectedTestCaseMain((prev) => {
      const currentSelection = Array.isArray(prev) ? prev : [];
      const mergedSelection = [...currentSelection];

      for (const testCase of testCases) {
        const resolvedId = testCase?.new_test_case_id ?? testCase?.test_case_id;
        const alreadySelected = mergedSelection.some(
          (selectedItem) =>
            String(selectedItem?.test_case_id) === String(resolvedId) ||
            (testCase?.original_test_case_id && String(selectedItem?.test_case_id) === String(testCase.original_test_case_id))
        );
        
        if (alreadySelected) {
          // If it was already selected with the old ID, update it in place
          const index = mergedSelection.findIndex(
            (item) => String(item?.test_case_id) === String(testCase?.original_test_case_id || testCase?.test_case_id)
          );
          if (index !== -1) {
            mergedSelection[index] = {
              ...mergedSelection[index],
              ...testCase,
              original_test_case_id: testCase?.original_test_case_id || testCase?.test_case_id,
              test_case_id: resolvedId,
              is_campaign_linked: true,
              is_component_linked: true,
              is_lr_linked: true,
            };
          }
        } else {
          mergedSelection.push({
            ...testCase,
            original_test_case_id: testCase?.original_test_case_id || testCase?.test_case_id,
            test_case_id: resolvedId,
            is_campaign_linked: true,
            is_component_linked: true,
            is_lr_linked: true,
          });
        }
      }

      setSelectedTestCaseForDisplay(mergedSelection);
      setSelectedSmartMatchTestCaseSuggestions(mergedSelection);
      return mergedSelection;
    });
  };

  const pipelineQueueRef = React.useRef([]);

  const runPipelineStep3 = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;
    addFullyLinkedTestCases(
      testCases.map((tc) => ({
        ...tc,
        is_campaign_linked: true,
        is_component_linked: true,
        is_lr_linked: true,
      })),
    );
  };

  const runPipelineStep2 = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) return;

    const updatedTestCases = testCases.map(tc => {
      if (tc?.new_test_case_id && String(tc.new_test_case_id) !== String(tc.test_case_id)) {
        return {
          ...tc,
          original_test_case_id: tc.test_case_id,
          test_case_id: tc.new_test_case_id  || tc.test_case_id,
        };
      }
      return tc;
    });

    const campaignUnlinked = updatedTestCases.filter(
      (tc) => tc?.is_campaign_linked === false,
    );
    const campaignLinked = updatedTestCases.filter(
      (tc) => tc?.is_campaign_linked !== false,
    );

    if (campaignLinked.length > 0) {
      runPipelineStep3(campaignLinked);
    }

    if (campaignUnlinked.length > 0) {
      setTestCasesToCampaignLink(campaignUnlinked);
      setOpenCampaignLinkDialog(true);
    }
  };

  const handleSelectedTestCases = (testCases = []) => {
    const currentSelectedIds = new Set(
      (selectedTestCaseMain || []).map((tc) => tc?.test_case_id),
    );

    const freshTestCases = testCases.filter(
      (tc) => !currentSelectedIds.has(tc?.test_case_id),
    );

    if (freshTestCases.length === 0) return;

    const componentUnlinked = freshTestCases.filter(
      (tc) =>
        tc?.is_component_linked === false || tc?.is_lr_linked === false,
    );
    const componentLinked = freshTestCases.filter(
      (tc) =>
        tc?.is_component_linked !== false && tc?.is_lr_linked !== false,
    );

    pipelineQueueRef.current = componentLinked;

    if (componentUnlinked.length > 0) {
      setTestCasesToComponentLink(componentUnlinked);
      setOpenComponentLinkDialog(true);
    } else {
      runPipelineStep2(componentLinked);
    }
  };

  const clearCampaignLinkFields = () => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
  };

  const openCampaignDialogForTestCases = (testCases = []) => {
    setTestCasesToCampaignLink(testCases);
    setOpenCampaignLinkDialog(true);
  };

  const handleSmartMatchRequest = async (componentDetails, tag) => {
    setDisableSmartMatch(true);
    setCloseAiDialogOnAdd(false);

    const rawItems = Array.isArray(componentDetails)
      ? componentDetails
      : componentDetails
      ? [componentDetails]
      : [];

    const fallbackItems =
      rawItems.length > 0
        ? rawItems
        : tag === ENTITY.TYPE.LEGAL
        ? selectedEEMain || []
        : selectedLegalMain || [];

    const descriptions = fallbackItems
      .map((item) => item?.description || item?.name)
      .filter(Boolean);

    const componentName =
      descriptions.length > 0
        ? descriptions.join(", ")
        : (selectedEEMain || [])
            .map((ee) => ee?.description || ee?.assembly_number)
            .filter(Boolean)
            .join(", ");

    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    } else {
      setComponentToBeMatched(["smart-match"]);
    }

    const sourceType =
      tag === ENTITY.TYPE.LEGAL ? ENTITY.TYPE.EE : ENTITY.TYPE.LEGAL;

    const source = fallbackItems
      .map((item) => {
        const id =
          tag === ENTITY.TYPE.LEGAL
            ? item?.ee_id || item?.id
            : item?.lr_id || item?.id;
        return {
          entityType: sourceType,
          entityId: String(id),
        };
      })
      .filter(
        (s) =>
          s.entityId &&
          s.entityId !== "undefined" &&
          s.entityId !== "null",
      );

    const shouldUseEEScope = tag === ENTITY.TYPE.TEST_CASE;

    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source,
      targetEntityTypes: [tag],
      topK: 20,
      minConfidence: 0.1,
      description: componentName,
      scope: shouldUseEEScope
        ? {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
          scopeEntities: (selectedEEMain || []).map((ee) => ({
            entityType: ENTITY.TYPE.EE,
            entityId: Number(ee?.ee_id || ee?.id),
          })),
        }
        : {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
        },
    });
    setDisableSmartMatch(false);
  };

  const getSuppliersList = (component) => {
    if (!component) return [];
    const suppliers = component.supplier_name || component.suppliers;
    if (Array.isArray(suppliers) && suppliers.length > 0) {
      return suppliers.map((name) => ({
        label:
          typeof name === "object"
            ? name?.name || name?.label || name?.value || "-"
            : String(name),
        value:
          typeof name === "object"
            ? name?.value || name?.name || "-"
            : String(name),
      }));
    }
    if (typeof suppliers === "string" && suppliers.trim().length > 0) {
      return [{ label: suppliers, value: suppliers }];
    }
    const fallbackSuppliers = (allUsersList || [])
      .filter(
        (u) => u?.role_details?.capability_code === CAPABILITY_CODE.supplier,
      )
      .map((u) => ({
        label: u.name || u.fullname || u.email || "Supplier",
        value: u.name || u.fullname || String(u.user_id),
      }));
    return fallbackSuppliers;
  };

  const handleAddPendingEEComponent = () => {
    if (pendingEEComponent) {
      const supplierValue = selectedSupplier || "";
      const componentWithSupplier = {
        ...pendingEEComponent,
        selected_supplier: supplierValue,
      };
      setSelectedEEMain((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const exists = safePrev.some(
          (item) => item?.ee_id === componentWithSupplier.ee_id,
        );
        return exists
          ? safePrev.map((item) =>
              item?.ee_id === componentWithSupplier.ee_id
                ? {
                    ...item,
                    ...componentWithSupplier,
                    selected_supplier: supplierValue,
                  }
                : item,
            )
          : [...safePrev, componentWithSupplier];
      });
      void getEELinkedLRList({ eeId: componentWithSupplier?.ee_id });
    }
    resetSupplierDialog();
  };

  const resetSupplierDialog = () => {
    setOpenSupplierDialog(false);
    setPendingEEComponent(null);
    setSelectedSupplier("");
  };

  const handleLinkSuggestions = async (recommendation) => {
    if (recommendation.type === "EE") {
      const eeId = recommendation?.ee_id || recommendation?.id;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.get_ee_components,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedVariantDetails?.variant_id),
              ee_id: Number(eeId),
              id: Number(eeId),
              page_number: 1,
              page_size: 1,
            },
            show_toast: false,
            ignore_app_loading_spinner: true,
          }),
        ),
      );
      const eeData = !resp.error && resp.data?.length ? resp.data[0] : null;
      const fullEE = {
        ...eeData,
        ...recommendation,
        ...(eeData || {}),
        ee_id: eeId,
        id: eeId,
        supplier_name:
          eeData?.supplier_name ||
          eeData?.suppliers ||
          recommendation?.supplier_name ||
          recommendation?.suppliers ||
          [],
      };
      setPendingEEComponent(fullEE);
      const options = getSuppliersList(fullEE);
      setSelectedSupplier(options.length === 1 ? options[0].value : "");
      setOpenSupplierDialog(true);
    } else if (recommendation.type === "LEGAL_REQUIREMENT") {
      handleLegalAddClick(recommendation);
    } else if (recommendation.type === "TEST_CASE") {
      handleTestCaseAddClick(recommendation);
    }
  };

  const handleLegalAddClick = (recommendation) => {
    void fetchLegalRecommendationContext({ lr_id: recommendation.lr_id });
  };

  const fetchLegalRecommendationContext = async (params) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          https_method: "GET",
          ...legalRequirements.get_legal_requirements_details_new,
          params: params,
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const legalRequirementData = resp.data[0];
      setSelectedLegalMain((prev) => {
        const current = prev || [];
        const exists = current.some(
          (item) => item?.lr_id === legalRequirementData?.lr_id,
        );
        const next = exists ? current : [...current, legalRequirementData];
        const allTechUsers = next.flatMap((lr) =>
          (lr?.tech_service_users || []).map((u) => ({
            ...u,
            chapter: lr?.chapter,
          })),
        );
        setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        return next;
      });
      setSelectedLegalForDisplay((prev) => {
        const current = prev || [];
        const exists = current.some(
          (item) => item?.lr_id === legalRequirementData?.lr_id,
        );
        return exists ? current : [...current, legalRequirementData];
      });
    }
  };

  const handleTestCaseAddClick = (recommendation) => {
    void fetchTestCaseRecommendationContext(
      {
        manual_test_case_id: recommendation.manual_test_case_id,
      },
      recommendation,
    );
  };

  const fetchTestCaseRecommendationContext = async (params, recommendation) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          https_method: "GET",
          ...testCasesActions.get_test_cases,
          params: params,
          show_toast: false,
          ignore_app_loading_spinner: true,
        }),
      ),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const testCaseData = resp.data[0];
      const mergedTestCase = {
        ...testCaseData,
        ...recommendation,
        ...testCaseData,
        is_component_linked:
          recommendation?.is_component_linked !== undefined
            ? recommendation.is_component_linked
            : testCaseData?.is_component_linked,
        is_lr_linked:
          recommendation?.is_lr_linked !== undefined
            ? recommendation.is_lr_linked
            : testCaseData?.is_lr_linked,
        is_campaign_linked:
          recommendation?.is_campaign_linked !== undefined
            ? recommendation.is_campaign_linked
            : testCaseData?.is_campaign_linked,
      };
      handleSelectedTestCases([mergedTestCase]);
    } else if (recommendation || params) {
      handleSelectedTestCases([recommendation || params]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      let resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...userManagementApis.get_users_list_pa,
            show_toast: false,
            params: { page_number: 1, page_size: 500 },
          }),
        ),
      );
      setAllUsersList(resp?.data || []);
    };
    void fetchUsers();
  }, [dispatch]);

  useEffect(() => {
    if (selectedSmartMatchTestCaseSuggestions.length > 0) {
      setSelectedTestCaseMain(selectedSmartMatchTestCaseSuggestions);
      setSelectedTestCaseForDisplay(selectedSmartMatchTestCaseSuggestions);
    }
  }, [selectedSmartMatchTestCaseSuggestions]);

  const getEELinkedLRList = async ({ eeId, eeIds }) => {
    const ids = eeIds || (eeId ? [eeId] : []);
    if (ids.length > 0) {
      let apiDetails = softwareIntersectionAPI.get_ee_legal_requirements;
      const responses = await Promise.all(
        ids.map((id) =>
          Promise.resolve(
            dispatch(
              HandleApiActions({
                ...apiDetails,
                params: {
                  project_id: Number(selectedProjectDetails.project_id),
                  variant_id: Number(selectedVariantDetails?.variant_id),
                  ee_id: Number(id),
                },
                show_toast: false,
              }),
            ),
          ),
        ),
      );

      const allLinkedLrIds = [];
      for (const resp of responses) {
        if (!resp?.error && resp?.data?.[0]?.linked_lr_details?.[0]?.lr_id) {
          for (const lr of resp.data[0].linked_lr_details) {
            allLinkedLrIds.push(Number(lr.lr_id));
          }
        }
      }
      setDisabledLegalRequirement(Array.from(new Set(allLinkedLrIds)));
    } else {
      setDisabledLegalRequirement([]);
    }
  };

  const getDisabledEEListForLR = async ({ lrId, lrIds }) => {
    const ids = lrIds || (lrId ? [lrId] : []);
    if (ids.length === 0) {
      setDisabledEEComponents([]);
      return;
    }

    if (
      !selectedProjectDetails?.project_id ||
      !selectedVariantDetails?.variant_id
    ) {
      setDisabledEEComponents([]);
      return;
    }

    const responses = await Promise.all(
      ids.map((id) =>
        Promise.resolve(
          dispatch(
            HandleApiActions({
              ...softwareIntersectionAPI.get_ee_for_legal_requirements,
              params: {
                project_id: Number(selectedProjectDetails?.project_id),
                variant_id: Number(selectedVariantDetails?.variant_id),
                lr_id: Number(id),
              },
              show_toast: false,
              ignore_app_loading_spinner: true,
            }),
          ),
        ),
      ),
    );

    const allEEIds = [];
    for (const resp of responses) {
      if (!resp?.error) {
        const eeIds = resp?.data?.[0]?.ee_ids || [];
        for (const eeId of eeIds) {
          allEEIds.push(Number(eeId));
        }
      }
    }
    const normalizedIds = Array.from(new Set(allEEIds))
      .map(Number)
      .filter((id) => Number.isFinite(id));
    setDisabledEEComponents(normalizedIds);
  };

  const [dynamicFormFilteredData, setDynamicFormFilteredData] = useState(null);
  const [showDynamicSelect, setShowDynamicSelect] = useState(false);
  useEffect(() => {
    setZoneList(selectedProjectDetails?.project_zones);
    setIsZonalProject(selectedProjectDetails?.is_zonal);
  }, [selectedProjectDetails]);

  const clearForm = () => {
    setFormResetKey((k) => k + 1);
    setSelectedEEMain([]);
    setSelectedLegalMain([]);
    setSelectedLegalForDisplay([]);
    setSelectedDynamicForm(null);
    setSelectedComplianceRequestors([]);
    setSelectedComplianceReviewers([]);
    setDescription("");
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedComponentSupplierDetails([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
    setSelectedComplianceSupplier([]);
    setDisabledEEComponents([]);
    clearCampaignLinkFields();
  };

  useEffect(() => {
    clearForm();
    setSavedLinkages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectDetails, selectedVariantDetails]);

  const resetTestCaseState = () => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  };

  useEffect(() => {
    resetTestCaseState();
  }, [selectedEEMain, selectedLegalMain]);

  useEffect(() => {
    if (selectedEEMain && selectedEEMain.length > 0) {
      const { requestors, suppliers } = extractUsersFromEEData(
        selectedEEMain,
        allUsersList,
      );
      setSelectedComponentRequestor(requestors);
      setSelectedComponentSupplierDetails(suppliers);
    } else {
      setSelectedComponentRequestor([]);
      setSelectedComponentSupplierDetails([]);
    }
  }, [selectedEEMain, allUsersList]);

  useEffect(() => {
    if (selectedLegalMain && selectedLegalMain.length > 0) {
      void getDisabledEEListForLR({ lrIds: selectedLegalMain.map((lr) => lr.lr_id) });
    } else {
      setDisabledEEComponents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLegalMain, selectedProjectDetails, selectedVariantDetails]);

  const handleLocalSaveForm = () => {
    const selectedIds = (selectedEEMain || []).map((ee) => ee.ee_id);
    const selectedLrIds = (selectedLegalMain || []).map((lr) => lr.lr_id);

    const existingEE = savedLinkages.find((item) =>
      hasSameEEs(item.ee_ids, selectedIds),
    );

    if (existingEE) {
      const duplicateLR = existingEE.legal.some((legal) => {
        const existingLrIds =
          legal.lr_ids || (legal.lr_id ? [legal.lr_id] : []);
        return selectedLrIds.some((id) => existingLrIds.includes(id));
      });
      if (duplicateLR) {
        alert("Unable to create duplicate ticket");
        return;
      }
      existingEE.legal.push({
        lr_ids: selectedLrIds,
        description: description,
        oem_users: selectedComplianceRequestors.map((user) => user.value),
        tech_service_users: selectedComplianceReviewers.map(
          (user) => user.value,
        ),
        suppliers: selectedComplianceSupplier.map((user) => user.value),

        test_case_ids:
          selectedTestCaseMain?.map((tc) => tc.test_case_id) || [],
        test_cases: selectedTestCaseMain || [],
        ...(showDynamicSelect
          ? { dynamic_form: dynamicFormFilteredData }
          : {}),
      });
      setSavedLinkages([...savedLinkages]);
    } else {
      setSavedLinkages([
        ...savedLinkages,
        {
          ee_ids: selectedIds,
          addOn: selectedEEMain,
          legal: [
            {
              lr_ids: selectedLrIds,
              description: description,
              oem_users: selectedComplianceRequestors.map(
                (user) => user.value,
              ),
              tech_service_users: selectedComplianceReviewers.map(
                (user) => user.value,
              ),
              suppliers: selectedComplianceSupplier.map(
                (user) => user.value,
              ),
              test_case_ids:
                selectedTestCaseMain?.map((tc) => tc.test_case_id) || [],
              test_cases: selectedTestCaseMain || [],
              ...(showDynamicSelect
                ? { dynamic_form: dynamicFormFilteredData }
                : {}),
            },
          ],
        },
      ]);
    }
    clearForm();
  };

  const handleLegalRequirementCheckboxChange = (checked, item) => {
    const currentSelection = selectedLegalMain || [];
    let newSelectedLegal;
    if (checked) {
      const alreadyExists = currentSelection.some(
        (selectedItem) => selectedItem?.lr_id === item?.lr_id,
      );
      newSelectedLegal = alreadyExists
        ? currentSelection
        : [...currentSelection, item];
    } else {
      newSelectedLegal = currentSelection.filter(
        (selectedItem) => selectedItem?.lr_id !== item?.lr_id,
      );
    }
    setSelectedLegalMain(newSelectedLegal);
    const allTechUsers = (newSelectedLegal || []).flatMap((lr) =>
      (lr?.tech_service_users || []).map((u) => ({
        ...u,
        chapter: lr?.chapter,
      })),
    );
    setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
  };

  const handleUnlinkSuggestions = (item) => {
    const type = item?.type || item?.raw?.targetContext?.entityType;
    const entityId = item?.ee_id ?? item?.lr_id ?? item?.test_case_id ?? item?.tc_id ?? item?.id;

    if (type === "EE" || item?.ee_id) {
      const targetEeId = item?.ee_id || entityId;
      setSelectedEEMain((prev) =>
        (prev || []).filter(
          (c) => String(c?.ee_id) !== String(targetEeId) && String(c?.id) !== String(targetEeId),
        ),
      );
    }
    if (type === "LEGAL_REQUIREMENT" || item?.lr_id) {
      const lrId = item?.lr_id || entityId;
      setSelectedLegalMain((prev) => {
        const next = (prev || []).filter(
          (c) => String(c?.lr_id) !== String(lrId),
        );
        const allTechUsers = next.flatMap((lr) =>
          (lr?.tech_service_users || []).map((u) => ({
            ...u,
            chapter: lr?.chapter,
          })),
        );
        setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        return next;
      });
      setSelectedLegalForDisplay((prev) =>
        (prev || []).filter((c) => String(c?.lr_id) !== String(lrId)),
      );
    }
    if (type === "TEST_CASE" || item?.test_case_id || item?.tc_id) {
      const tcId = item?.test_case_id || item?.tc_id || entityId;
      const tcNumber = item?.manual_test_case_id || item?.req_number;
      setSelectedTestCaseMain((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
      setSelectedTestCaseForDisplay((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
      setSelectedSmartMatchTestCaseSuggestions((prev) =>
        (prev || []).filter(
          (c) =>
            String(c?.test_case_id) !== String(tcId) &&
            (!c?.original_test_case_id || String(c?.original_test_case_id) !== String(tcId)) &&
            (!tcNumber || String(c?.manual_test_case_id) !== String(tcNumber)),
        ),
      );
    }
  };

  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ borderBottom: "1px solid black" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Select Entities and Fill in the Details
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12} sx={{ pt: 1 }}>
          <RaiseTicketTemplate
            leftContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                <Box>
                  <SelectTemplate
                    heading={"Select EE Component"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenEESelect(true);
                    }}
                    enableEdit={selectedEEMain && selectedEEMain.length > 0}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedLegalMain && selectedLegalMain.length > 0}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(selectedLegalMain, ENTITY.TYPE.EE)
                    }
                    content={
                      selectedEEMain && selectedEEMain.length > 0 ? (
                        <Box sx={{ pl: 2, display: "flex", flexDirection: "column", gap: 1.5, maxHeight: "200px", overflowY: "auto" }}>
                          {selectedEEMain.map((ee, idx) => (
                            <KeyValueDisplayCard
                              key={ee.ee_id || idx}
                              data={[
                                {
                                  key: "Number",
                                  value: ee.assembly_number,
                                  style: { fontWeight: 600 },
                                },
                                {
                                  key: "Description",
                                  value: ee.description,
                                },
                                {
                                  key: "Version",
                                  value: ee.version,
                                },
                              ]}
                            />
                          ))}
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Legal Requirement"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenLegalSelect(true);
                    }}
                    enableEdit={selectedLegalForDisplay && selectedLegalForDisplay.length > 0}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedEEMain && selectedEEMain.length > 0}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(selectedEEMain, ENTITY.TYPE.LEGAL)
                    }
                    content={
                      selectedLegalForDisplay && selectedLegalForDisplay.length > 0 ? (
                        <Box sx={{ pl: 2, maxHeight: "200px", overflowY: "auto" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "left",
                              pl: 1,
                              width: "100%",
                            }}
                          >
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                              }}
                            >
                              Legal Standard:
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Name: ${selectedLegalForDisplay[0]?.legal_standard_details?.name || ""}`}
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Number: ${selectedLegalForDisplay[0]?.legal_standard_details?.number || ""}`}
                            </Typography>
                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              {`Revision: ${selectedLegalForDisplay[0]?.legal_standard_details?.revision || ""}`}
                            </Typography>

                            <Typography
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                                mt: 1,
                              }}
                            >
                              Legal Requirements:
                            </Typography>
                            {selectedLegalForDisplay.map((lr, idx) => (
                              <Box
                                key={lr.lr_id || idx}
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  position: "relative",
                                  mb: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "10%",
                                  }}
                                >
                                  <CustomCheckBoxWithoutFormControl
                                    value={
                                      selectedLegalMain?.some(
                                        (selectedItem) =>
                                          selectedItem?.lr_id === lr?.lr_id,
                                      ) || false
                                    }
                                    onChange={(e) =>
                                      handleLegalRequirementCheckboxChange(
                                        e.target.checked,
                                        lr,
                                      )
                                    }
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    width: "90%",
                                    pl: 1,
                                    borderLeft: "2px solid #ccc",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      textAlign: "left",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {`Number: ${lr.req_number || ""}`}
                                  </Typography>
                                  <CustomToolTip
                                    length={200}
                                    style={{
                                      ...componentStyle.font.text,
                                      textAlign: "left",
                                      width: "100%",
                                    }}
                                  >
                                    {`Description: ${lr.description || ""}`}
                                  </CustomToolTip>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Test Case"}
                    onClickAdd={() => {
                      setOpenTestCaseSelect(true);
                    }}
                    disableSmartMatch={disableSmartMatch}
                     addButtonDisabled={
                      !selectedLegalMain || selectedLegalMain.length === 0 || !selectedEEMain || selectedEEMain.length === 0
                    }
                    enableEdit={
                      selectedTestCaseForDisplay !== null &&
                      selectedTestCaseForDisplay.length > 0
                    }
                    showSmartMatch={
                      selectedEEMain && selectedEEMain.length > 0 && selectedLegalMain && selectedLegalMain.length > 0
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.TEST_CASE,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        {selectedTestCaseForDisplay?.length > 0 &&
                          selectedTestCaseForDisplay.map((item) => (
                            <Box
                              key={item.test_case_id}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <CustomCheckBoxWithoutFormControl
                                  value={
                                    selectedTestCaseMain?.some(
                                      (selectedItem) =>
                                        selectedItem?.test_case_id ===
                                        item?.test_case_id,
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    const currentSelection = selectedTestCaseMain || [];
                                    if (e.target.checked) {
                                      if (item?.is_component_linked === false || item?.is_lr_linked === false) {
                                        setTestCasesToComponentLink([item]);
                                        setOpenComponentLinkDialog(true);
                                        return;
                                      }
                                      if (item?.is_campaign_linked === false) {
                                        openCampaignDialogForTestCases([item]);
                                        return;
                                      }
                                      const alreadyExists = currentSelection.some(
                                        (selectedItem) => selectedItem?.test_case_id === item?.test_case_id
                                      );
                                      if (!alreadyExists) {
                                        const newSelectedTestCase = [...currentSelection, item];
                                        setSelectedTestCaseMain(newSelectedTestCase);
                                        setSelectedTestCaseForDisplay(newSelectedTestCase);
                                      }
                                    } else {
                                      const newSelectedTestCase = currentSelection.filter(
                                        (selectedItem) => selectedItem?.test_case_id !== item?.test_case_id
                                      );
                                      setSelectedTestCaseMain(newSelectedTestCase);
                                      setSelectedTestCaseForDisplay(newSelectedTestCase);
                                    }
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  width: "60%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case ID: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.manual_test_case_id}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case Title: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.name}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Description: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.description}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "30%",
                                }}
                              >
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            }
            rightContent={
              <RaiseTicketRightTemplate
                selectedComponentRequestor={selectedComponentRequestor}
                selectedLegalTechUserDetails={selectedLegalTechUserDetails}
                selectedComplianceRequestors={selectedComplianceRequestors}
                setSelectedComplianceRequestor={setSelectedComplianceRequestors}
                selectedComplianceReviewers={selectedComplianceReviewers}
                setSelectedComplianceReviewers={setSelectedComplianceReviewers}
                description={description}
                setDescription={setDescription}
                selectedComponentSupplierDetails={
                  selectedComponentSupplierDetails
                }
                setSelectedComponentSupplierDetails={
                  setSelectedComponentSupplierDetails
                }
                selectedEEMain={selectedEEMain}
                selectedComponent={selectedEEMain}
                selectedComplianceSupplier={selectedComplianceSupplier}
                setSelectedComplianceSupplier={setSelectedComplianceSupplier}
                formType={entityType}
                selectedDynamicForm={selectedDynamicForm}
                setSelectedDynamicForm={setSelectedDynamicForm}
                dynamicFormFilteredData={dynamicFormFilteredData}
                setDynamicFormFilteredData={setDynamicFormFilteredData}
                showDynamicSelect={showDynamicSelect}
                setShowDynamicSelect={setShowDynamicSelect}
                 localSubmitDisabled={
                  !selectedEEMain ||
                  selectedEEMain.length === 0 ||
                  !selectedLegalMain ||
                  selectedLegalMain.length === 0 ||
                  description === "" ||
                  selectedComplianceRequestors.length === 0 ||
                  description.trim().length < 3 ||
                  selectedComplianceReviewers.length === 0
                }
                handleLocalSaveForm={handleLocalSaveForm}
                savedLinkageCard={
                  <EESWMSavedLinkageCard
                    savedLinkages={savedLinkages}
                    setSavedLinkages={setSavedLinkages}
                    onRemove={clearForm}
                  />
                }
              />
            }
          />
        </Grid>
      </Grid>

      <DialogIndex
        key={formResetKey}
        openEESelect={openEESelect}
        setOpenEESelect={setOpenEESelect}
        isZonalProject={isZonalProject}
        zoneList={zoneList}
        savedLinkages={savedLinkages}
        disableLrRadioButton={(item) => {
          const selectedIds = new Set((selectedEEMain || []).map((ee) => ee.ee_id));
          const eeExists = savedLinkages.find((linkage) => {
            return linkage.ee_ids?.some((id) => selectedIds.has(id));
          });
          return eeExists
            ? eeExists.legal.some((legal) => legal.lr_id === item.lr_id)
            : false;
        }}
        selectedEEMain={selectedEEMain}
        onClickAdd={async (data) => {
          if (selectedLegalMain && selectedLegalMain.length > 0) {
            const entity = (data || []).map((ee) => ({
              id: Number(ee.ee_id),
              supplier_name: ee.selected_supplier || (Array.isArray(ee.supplier_name) ? ee.supplier_name[0] : ee.supplier_name) || ""
            }));
            const lrIds = selectedLegalMain.map((x) => Number(x.lr_id));
            const duplicateCheck = await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...typeApprovalAPI.check_for_duplicate,
                  params: {
                    ticket_type: 2,
                    entity: entity,
                    lr_ids: lrIds,
                  },
                  show_toast: false,
                  ignore_app_loading_spinner: true,
                }),
              ),
            );
            if (duplicateCheck && !duplicateCheck.error && duplicateCheck.data?.intr_id?.length > 0) {
              setDuplicateAlertMessage(`Ticket for selected EE component and legal requirement combination already exist in intersection id : ${duplicateCheck.data.intr_id.join(", ")}`);
              setOpenDuplicateAlert(true);
              return;
            }
          }

          setSelectedEEMain(data);

          if (Array.isArray(data) && data.length > 0) {
            await getEELinkedLRList({ eeIds: data.map((x) => x.ee_id) });
          } else {
            await getEELinkedLRList({ eeId: data?.ee_id });
          }
          setOpenEESelect(false);

          const { requestors, suppliers } = extractUsersFromEEData(
            data,
            allUsersList,
          );
          setSelectedComponentRequestor(requestors);
          setSelectedComponentSupplierDetails(suppliers);
        }}
        openLegalSelect={openLegalSelect}
        setOpenLegalSelect={setOpenLegalSelect}
        selectedLegalMain={selectedLegalMain}
        disabledLegalRequirement={disabledLegalRequirement}
        setSelectedLegalMain={setSelectedLegalMain}
        onClickAddLegal={async (data) => {
          if (selectedEEMain && selectedEEMain.length > 0) {
            const entity = selectedEEMain.map((ee) => ({
              id: Number(ee.ee_id),
              supplier_name: ee.selected_supplier || (Array.isArray(ee.supplier_name) ? ee.supplier_name[0] : ee.supplier_name) || ""
            }));
            const lrIds = (data || []).map((x) => Number(x.lr_id));
            const duplicateCheck = await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...typeApprovalAPI.check_for_duplicate,
                  params: {
                    ticket_type: 2,
                    entity: entity,
                    lr_ids: lrIds,
                  },
                  show_toast: false,
                  ignore_app_loading_spinner: true,
                }),
              ),
            );
            if (duplicateCheck && !duplicateCheck.error && duplicateCheck.data?.intr_id?.length > 0) {
              setDuplicateAlertMessage(`Ticket for selected EE component and legal requirement combination already exist in intersection id : ${duplicateCheck.data.intr_id.join(", ")}`);
              setOpenDuplicateAlert(true);
              return;
            }
          }

          setSelectedLegalMain(data);
          setSelectedLegalForDisplay(data);
          setOpenLegalSelect(false);
          const allTechUsers = (data || []).flatMap((lr) => (lr?.tech_service_users || []).map(u => ({...u, chapter: lr?.chapter})));
          setSelectedLegalTechUserDetails(removeDuplicates(allTechUsers));
        }}
        openTestCaseSelect={openTestCaseSelect}
        setOpenTestCaseSelect={setOpenTestCaseSelect}
        selectedTextCaseMain={selectedTestCaseMain}
        setSelectedTestCaseMain={setSelectedTestCaseMain}
        onClickAddTestCase={(data) => {
          handleSelectedTestCases(data);
          setOpenTestCaseSelect(false);
        }}
        entityID={selectedEEMain?.[0]?.ee_id}
        entityType={"ee"}
        prefillContext={prefillContext}
        aiRecommendedEntities={[]}
        disabledEEComponents={disabledEEComponents}
      />
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleLinkSuggestions}
          onUnlinkRequest={handleUnlinkSuggestions}
          closeDialogOnAdd={closeAiDialogOnAdd}
          selectedComponents={selectedEEMain}
          selectedLegals={selectedLegalMain}
          selectedTestCases={selectedTestCaseMain}
        />
      )}
      <TestCaseCampaignLinkDialog
        open={openCampaignLinkDialog}
        testCasesToLink={testCasesToCampaignLink}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        onSuccess={(linkedTestCases) => {
          clearCampaignLinkFields();
          runPipelineStep3(
            (linkedTestCases || []).map((tc) => ({
              ...tc,
              is_campaign_linked: true,
            })),
          );
        }}
      />
      <TestCaseComponentLinkDialog
        open={openComponentLinkDialog}
        testCasesToLink={testCasesToComponentLink}
        selectedEEMain={selectedEEMain}
        selectedLegalMain={selectedLegalMain}
        onClose={() => setOpenComponentLinkDialog(false)}
        onSuccess={(linkedTestCases) => {
          setOpenComponentLinkDialog(false);
          const componentLinkedCases = (linkedTestCases || []).map((tc) => ({
            ...tc,
            is_component_linked: true,
            is_lr_linked: true,
          }));
          const allCasesForStep2 = [
            ...pipelineQueueRef.current,
            ...componentLinkedCases,
          ];
          pipelineQueueRef.current = [];
          runPipelineStep2(allCasesForStep2);
        }}
      />
      <CustomDialogBoxV2
        open={openDuplicateAlert}
        onClose={() => setOpenDuplicateAlert(false)}
        title="Duplicate Ticket Alert"
        hideDefaultActions={true}
        footerSlot={
          <DialogActions sx={{ pr: 3, pl: 3, pb: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setOpenDuplicateAlert(false)}
              variant="contained"
              sx={{
                backgroundColor: "black",
                color: "white",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        }
      >
        <Typography>{duplicateAlertMessage}</Typography>
      </CustomDialogBoxV2>
      <CustomDialogBoxV2
        title="Select Supplier"
        open={openSupplierDialog}
        onClose={resetSupplierDialog}
        sx={{ zIndex: 1400 }}
        dialogActions={
          <SelectDialogActionButtons
            enableSubmit={
              Boolean(selectedSupplier) ||
              getSuppliersList(pendingEEComponent).length === 0
            }
            onClickAdd={handleAddPendingEEComponent}
            onClickCancel={resetSupplierDialog}
          />
        }
      >
        <Box sx={{ p: 2, minWidth: "300px" }}>
          <CustomSelectV2
            label="Select Supplier"
            optionsList={getSuppliersList(pendingEEComponent)}
            selectedValue={selectedSupplier}
            handleOnChange={(e) => setSelectedSupplier(e.target.value)}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

RaiseEETicket.propTypes = {
  savedLinkages: PropTypes.array,
  setSavedLinkages: PropTypes.func,
  setDisableRaiseTicketButton: PropTypes.func,
  entityType: PropTypes.string,
};

export default RaiseEETicket;
